/**
 * Contacts API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Contact,
  ContactCreateData,
  ContactUpdateData,
  ContactFilters,
  Activity,
} from '@/types'

export const contactsApi = {
  /**
   * List contacts with filtering and pagination
   */
  async list(params?: PaginationParams & ContactFilters): Promise<PaginatedResponse<Contact>> {
    const response = await apiClient.get<PaginatedResponse<Contact>>('/contacts', { params })
    return response.data
  },

  /**
   * Get contact by ID
   */
  async get(id: string): Promise<Contact> {
    const response = await apiClient.get<ApiResponse<Contact>>(`/contacts/${id}`)
    return response.data.data
  },

  /**
   * Create a new contact
   */
  async create(data: ContactCreateData): Promise<Contact> {
    const response = await apiClient.post<ApiResponse<Contact>>('/contacts', data)
    return response.data.data
  },

  /**
   * Update a contact
   */
  async update(id: string, data: ContactUpdateData): Promise<Contact> {
    const response = await apiClient.put<ApiResponse<Contact>>(`/contacts/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a contact
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/contacts/${id}`)
  },

  /**
   * Get contact activities
   */
  async getActivities(id: string, params?: PaginationParams): Promise<PaginatedResponse<Activity>> {
    const response = await apiClient.get<PaginatedResponse<Activity>>(`/contacts/${id}/activities`, { params })
    return response.data
  },

  /**
   * Add tags to contact
   */
  async addTags(id: string, tags: string[]): Promise<Contact> {
    const response = await apiClient.post<ApiResponse<Contact>>(`/contacts/${id}/tags`, { tags })
    return response.data.data
  },

  /**
   * Remove tag from contact
   */
  async removeTag(id: string, tag: string): Promise<Contact> {
    const response = await apiClient.delete<ApiResponse<Contact>>(`/contacts/${id}/tags/${encodeURIComponent(tag)}`)
    return response.data.data
  },

  /**
   * Import contacts from CSV
   */
  async import(file: File): Promise<{ imported: number; errors: string[] }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<ApiResponse<{ imported: number; errors: string[] }>>(
      '/contacts/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.data
  },

  /**
   * Lookup contact by email address
   */
  async lookupByEmail(email: string): Promise<Contact | null> {
    try {
      const response = await apiClient.get<ApiResponse<Contact>>(`/contacts/lookup`, {
        params: { email },
      })
      return response.data.data
    } catch {
      return null
    }
  },

  /**
   * Export contacts to CSV
   */
  async export(filters?: ContactFilters): Promise<Blob> {
    const response = await apiClient.get('/contacts/export', {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },
}
